import { useQuery } from "@tanstack/react-query";
import { tmdbFetch } from "./apiHelper";
import { replaceUrlParams } from "./apiHelper";
import { TMDBResponse } from "./types";

interface UseTMDBQueryOptions {
  /** URL template from TMDB_ENDPOINTS_STATIC */
  template: string;
  /** Parameters to replace in the template */
  params?: Record<string, string | number>;
  /** Additional headers to send with the request */

  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
    retry?: boolean | number;
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean;
    refetchInterval?: number;
  };
  /** Bearer token for authentication (optional, will use default if not provided) */
  bearerToken?: string;
}

/**
 * Generic TMDB Query Hook
 *
 * A flexible hook that can query any TMDB endpoint using URL templates.
 * Supports custom headers, authentication, and all TanStack Query options.
 *
 * @param options - Query configuration options
 * @returns TanStack Query result with TMDB data
 *
 * @example
 * ```typescript
 * // Basic usage with TMDB endpoints
 * const { data, isLoading, error } = useTMDBQuery({
 *   template: TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS,
 *   params: { tmdb_base_url: "https://api.themoviedb.org/3", id: 550 }
 * });
 *
 * // With custom headers
 * const { data } = useTMDBQuery({
 *   template: TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS,
 *   params: { tmdb_base_url: "https://api.themoviedb.org/3", id: 550 },
 *   headers: { "X-Custom-Header": "value" }
 * });
 *
 * // With query options
 * const { data } = useTMDBQuery({
 *   template: TMDB_ENDPOINTS_STATIC.TV_SHOWS.SEASON_DETAILS,
 *   params: { tmdb_base_url: "https://api.themoviedb.org/3", tvId: 1399, seasonNumber: 1 },
 *   queryOptions: {
 *     staleTime: 1000 * 60 * 5, // 5 minutes
 *     enabled: !!tvShowId,
 *     retry: 2
 *   }
 * });
 *
 * // Using the convenience function
 * const { data } = useTMDBQuery({
 *   template: TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS,
 *   params: { id: 550 } // tmdb_base_url will be added automatically
 * });
 * ```
 */
export function useTMDBQuery<T = unknown>({
  template,
  params = {},
  queryOptions = {},
  bearerToken,
}: UseTMDBQueryOptions) {
  // Create the final URL by replacing parameters
  const url = replaceUrlParams(template, params);

  // Create query key based on URL and important params
  const queryKey = ["tmdb", url];

  return useQuery({
    queryKey,
    queryFn: async (): Promise<T> => {
      return await tmdbFetch(url, {}, bearerToken);
    },
    // Default query options
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: false, // Consistent with other TMDB hooks
    // Merge with user-provided options
    ...queryOptions,
  });
}

/**
 * Convenience hook for TMDB queries with automatic base URL
 *
 * This is a shorthand for the most common use case where you want to query
 * TMDB with the standard base URL.
 *
 * @param template - URL template from TMDB_ENDPOINTS_STATIC
 * @param params - Parameters to replace (tmdb_base_url will be added automatically)
 * @param options - Additional query options
 * @returns TanStack Query result with TMDB data
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useTMDBQuerySimple(
 *   TMDB_ENDPOINTS_STATIC.MOVIES.DETAILS,
 *   { id: 550 }
 * );
 * ```
 */
export function useTMDBQuerySimple<T>({
  template,
  params,
  options = {},
}: {
  template: string;
  params?: Record<string, string | number>;
  options?: Omit<UseTMDBQueryOptions, "template" | "params">;
}) {
  return useTMDBQuery<TMDBResponse<T>>({
    template,
    params: {
      tmdb_base_url: "https://api.themoviedb.org/3",
      ...params,
    },
    ...options,
  });
}
